import React from 'react';

import { css, cx } from '@emotion/css';
import { Card, CardActions, CardContent, CardHeader } from '@mui/material';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons.ts';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { ChatVisibilitySelector } from '@/uiKit/molecules/chat/ChatVisibilitySelector.tsx';
import { MessagesList } from '@/uiKit/molecules/chat/MessagesList.tsx';
import { SendMessageForm } from '@/uiKit/molecules/chat/SendMessageForm.tsx';
import { useChatMessagingState } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';

export type ChatMessagesProps = {
  className?: string;
};

export const ChatMessages: React.FC<ChatMessagesProps> = ({ className }) => {
  const chat = useChatMessagingState(state => state);
  const styles = useStyles(makeStyles);
  const patientId = usePatientIdFromURL();

  if (!chat.isOpen || !patientId) {
    return null;
  }

  return (
    <Card className={cx(styles.container, className)}>
      <CardHeader
        title={<ChatVisibilitySelector />}
        action={
          <Icons.closeIcon
            className={styles.icons}
            onClick={() => chat.toggleChat()}
          />
        }
        className={styles.header}
      />
      <QueryAwait
        errorFallback={HandledErrorPage}
        pendingFallback={CenteredLoader}
      >
        <CardContent className={styles.messages}>
          <MessagesList visibility={chat.visibility} patientId={patientId} />
        </CardContent>
        <CardActions className={styles.actions}>
          <SendMessageForm className={styles.sendMessage} />
        </CardActions>
      </QueryAwait>
    </Card>
  );
};

const makeStyles = () => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    width: 'min(500px, 95vw)',
  }),
  header: css({
    '.MuiCardHeader-action': {
      alignSelf: 'center',
    },
  }),
  messages: css({
    flex: '1 1',
    overflowY: 'auto',
    scrollbarColor: 'lightgrey transparent',
    '::-webkit-scrollbar': {
      backgroundColor: 'transparent',
    },
  }),
  icons: css({
    width: FontSizes.body,
    height: FontSizes.body,
    cursor: 'pointer',
  }),
  actions: css({
    padding: 0,
  }),
  sendMessage: css({
    flex: '1 1',
  }),
});
